import { type SocketMachineContext } from '../../../network-state-machine-context.ts';
import {
  type ReconciledEvent,
  type SyncEvent,
  type SyncFetchingEvent,
} from '../../../network-state-machine-events.ts';

export async function syncingFetchSnapshotAsync<
  T extends Pick<
    SocketMachineContext,
    'socketFetchSnapshot' | 'relaySupplier' | 'debounce'
  >,
>(ctx: T, signal: AbortSignal): Promise<SyncFetchingEvent> {
  const remoteDocument = await ctx.socketFetchSnapshot(signal);

  const relayDetails = ctx.relaySupplier(
    ctx.debounce.onlineAttempts,
    signal,
    remoteDocument.collabStrategy,
  );

  return {
    type: 'synced',
    remoteDocument,
    relayDetails,
  };
}

export async function syncingFetchStepsAsync<
  T extends Pick<
    SocketMachineContext,
    'relayDetails' | 'remoteDocument' | 'socketFetchSteps' | 'debounce'
  >,
>(ctx: T, signal: AbortSignal): Promise<SyncFetchingEvent> {
  if (!ctx.relayDetails) {
    throw new Error('Missing relay details in context');
  }
  if (!ctx.remoteDocument) {
    throw new Error('Missing remote document in context');
  }

  const remoteDocument = await ctx.socketFetchSteps(
    ctx.remoteDocument,
    ctx.debounce.onlineAttempts,
    signal,
  );

  return {
    type: 'synced',
    remoteDocument,
    relayDetails: ctx.relayDetails,
  };
}

export async function syncingApplyChangesAsync<
  T extends Pick<
    SocketMachineContext,
    'relayDetails' | 'remoteDocument' | 'socketApplyChanges'
  >,
>(ctx: T): Promise<SyncFetchingEvent> {
  if (!ctx.relayDetails) {
    throw new Error('Missing relay details in context');
  }
  if (!ctx.remoteDocument) {
    throw new Error('Missing remote document in context');
  }

  const remoteDocument = ctx.socketApplyChanges(ctx.relayDetails.url, ctx.remoteDocument);

  return {
    type: 'synced',
    remoteDocument,
    relayDetails: ctx.relayDetails,
  };
}

export async function reconcileOfflineChangesAsync<
  T extends Pick<SocketMachineContext, 'reconcileOfflineChanges' | 'remoteDocument'>,
>(ctx: T): Promise<ReconciledEvent> {
  if (ctx.remoteDocument) {
    await ctx.reconcileOfflineChanges?.(ctx.remoteDocument);
  }

  return {
    type: 'reconciled',
  };
}

export async function syncingUpdateChangesAsync<
  T extends Pick<
    SocketMachineContext,
    | 'remoteDocument'
    | 'socketUpdateChanges'
    | 'debounce'
    | 'pendingMessages'
    | 'transport'
  >,
>(ctx: T, signal: AbortSignal): Promise<SyncEvent> {
  if (!ctx.remoteDocument) {
    throw new Error('Missing remote document in context');
  }

  // Tear down any stale transport from a previous connection attempt before
  // calling socketUpdateChanges. If a stale transport exists, its C3 client
  // will immediately process the 'send' event fired by sendUpdate() inside
  // socketUpdateChanges, calling retrieve() before resetAndLoad() has even run
  // — resulting in an empty buffer being sent to NCS. Clearing ctx.transport
  // here ensures the 'send' event is queued (not immediately consumed) and
  // will be properly drained by onEnterConnected after the new transport is created.
  if (ctx.transport != null) {
    ctx.transport = undefined;
  }

  ctx.socketUpdateChanges(ctx.remoteDocument, ctx.debounce.onlineAttempts, signal);

  return {
    type: 'synced',
  };
}

export function syncingApplyChangesStepFailedAsync<
  T extends Pick<
    SocketMachineContext,
    'relayDetails' | 'remoteDocument' | 'socketApplyChanges' | 'error'
  >,
>(ctx: T): Promise<SyncEvent> {
  if (!ctx.relayDetails) {
    throw new Error('Missing relay details in context');
  }
  if (!ctx.remoteDocument) {
    throw new Error('Missing remote document in context');
  }

  ctx.socketApplyChanges(ctx.relayDetails.url, ctx.remoteDocument);

  if (!ctx.error) {
    throw new Error('Missing error in context');
  }

  throw ctx.error;
}
